import React, { useState, useEffect } from 'react';
import { 
  subscribeToPush, 
  unsubscribeFromPush, 
  isSubscribed, 
  getNotificationPermission,
  isPushSupported,
  showLocalNotification 
} from '../utils/pushNotifications';
import { runFullDiagnostic, testLocalNotification } from '../utils/pushDebug';
import './PushNotificationSettings.css';

function PushNotificationSettings({ user }) {
  const [isNotificationEnabled, setIsNotificationEnabled] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = async () => {
    try {
      if (!isPushSupported()) {
        setError('Push notifications are not supported in this browser');
        return;
      }

      const currentPermission = Notification.permission;
      setPermission(currentPermission);

      if (currentPermission === 'granted') {
        const subscribed = await isSubscribed();
        setIsNotificationEnabled(subscribed);
      }
    } catch (err) {
      console.error('Error checking notification status:', err);
      setError('Failed to check notification status');
    }
  };

  const handleToggleNotifications = async () => {
    if (!user) {
      setError('Please log in to enable notifications');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isNotificationEnabled) {
        // Disable notifications
        await unsubscribeFromPush(user.id);
        setIsNotificationEnabled(false);
      } else {
        // Enable notifications
        const currentPermission = await getNotificationPermission();
        
        if (currentPermission === 'granted') {
          console.log('Notification permission granted, subscribing...');
          await subscribeToPush(user.id);
          setIsNotificationEnabled(true);
          setPermission('granted');
          
          // Show success notification
          showLocalNotification('Notifications Enabled! 🔔', {
            body: 'You will now receive push notifications for messages and friend requests',
            icon: '/default-avatar.svg'
          });
        } else if (currentPermission === 'denied') {
          setError('Notifications are blocked. Please enable them in your browser settings.');
          setPermission('denied');
        } else {
          setError('Notification permission was not granted');
          setPermission('denied');
        }
      }
    } catch (err) {
      console.error('Error toggling notifications:', err);
      console.error('Error details:', err.message);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to toggle notifications. ';
      
      if (err.message.includes('VAPID')) {
        errorMessage = 'Server configuration error. Please contact support.';
      } else if (err.message.includes('Service Worker')) {
        errorMessage = 'Service worker registration failed. Please refresh the page and try again.';
      } else if (err.message.includes('permission')) {
        errorMessage = 'Notification permission denied. Please enable notifications in your browser settings.';
      } else if (err.message.includes('not supported')) {
        errorMessage = 'Push notifications are not supported in your browser.';
      } else if (err.message.includes('Browser push service error')) {
        // This is the AbortError: Registration failed - push service error
        errorMessage = 'Push service temporarily unavailable. Please wait 1-2 minutes and try again. If problem persists, the VAPID keys may need to be regenerated on the server.';
      } else if (err.message.includes('Registration failed') || err.name === 'AbortError') {
        errorMessage = 'Push registration failed. This is usually temporary. Wait 1 minute and try again.';
      } else if (err.message.includes('subscription')) {
        errorMessage = 'Failed to save notification subscription. Please check your internet connection and try again.';
      } else {
        errorMessage += err.message || 'Please try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const testNotification = () => {
    if (permission === 'granted') {
      const success = testLocalNotification();
      if (!success) {
        setError('Failed to show test notification');
      }
    } else {
      setError('Please enable notifications first');
    }
  };

  const runDiagnostic = async () => {
    try {
      const diagnostic = await runFullDiagnostic();
      //console.log('Full diagnostic completed:', diagnostic);
      
      // Show diagnostic summary
      const summary = `
Browser: ${diagnostic.browser.browser}
Service Worker: ${diagnostic.support.serviceWorker ? '✅' : '❌'}
Push Manager: ${diagnostic.support.pushManager ? '✅' : '❌'}
Notifications: ${diagnostic.support.notifications ? '✅' : '❌'}
Permission: ${diagnostic.support.permission}
Active Registrations: ${diagnostic.serviceWorker.registrations.length}
Has Subscription: ${diagnostic.pushSubscription.hasSubscription ? '✅' : '❌'}
      `.trim();
      
      alert(`Push Notification Diagnostic:\n\n${summary}\n\nCheck console for detailed report.`);
    } catch (error) {
      console.error('Diagnostic failed:', error);
      setError('Diagnostic failed. Check console for details.');
    }
  };

  const getPermissionText = () => {
    switch (permission) {
      case 'granted':
        return 'Allowed';
      case 'denied':
        return 'Blocked';
      default:
        return 'Not set';
    }
  };

  const getPermissionColor = () => {
    switch (permission) {
      case 'granted':
        return '#4CAF50';
      case 'denied':
        return '#f44336';
      default:
        return '#ff9800';
    }
  };

  if (!isPushSupported()) {
    return (
      <div className="push-settings">
        <div className="push-settings-header">
          <h3>🔔 Push Notifications</h3>
        </div>
        <div className="push-settings-content">
          <div className="not-supported">
            <p>Push notifications are not supported in this browser.</p>
            <small>Try using Chrome, Firefox, or Safari for the best experience.</small>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="push-settings">
      <div className="push-settings-header">
        <h3>🔔 Push Notifications</h3>
        <div className="permission-status">
          <span 
            className="permission-badge"
            style={{ backgroundColor: getPermissionColor() }}
          >
            {getPermissionText()}
          </span>
        </div>
      </div>

      <div className="push-settings-content">
        <div className="setting-item">
          <div className="setting-info">
            <h4>Enable Push Notifications</h4>
            <p>Get notified when you receive new messages or friend requests</p>
          </div>
          <div className="setting-control">
            <button 
              className={`toggle-btn ${isNotificationEnabled ? 'enabled' : 'disabled'}`}
              onClick={handleToggleNotifications}
              disabled={loading}
            >
              {loading ? (
                <span className="loading-spinner">⏳</span>
              ) : (
                <span className="toggle-text">
                  {isNotificationEnabled ? 'ON' : 'OFF'}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* {permission === 'granted' && (
          <div className="setting-item">
            <div className="setting-info">
              <h4>Test Notifications</h4>
              <p>Send a test notification to make sure everything works</p>
            </div>
            <div className="setting-control">
              <button className="test-btn" onClick={testNotification}>
                Send Test
              </button>
            </div>
          </div>
        )} */}

        {/* <div className="setting-item">
          <div className="setting-info">
            <h4>Debug Information</h4>
            <p>Run diagnostic to troubleshoot notification issues</p>
          </div>
          <div className="setting-control">
            <button className="debug-btn" onClick={runDiagnostic}>
              Run Diagnostic
            </button>
          </div>
        </div> */}

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {permission === 'denied' && (
          <div className="help-section">
            <h4>How to enable notifications:</h4>
            <ol>
              <li>Click the 🔒 or ℹ️ icon in your browser's address bar</li>
              <li>Find "Notifications" in the permissions list</li>
              <li>Change it from "Block" to "Allow"</li>
              <li>Refresh this page and try again</li>
            </ol>
          </div>
        )}

        <div className="notification-info">
          <h4>What you'll get notified about:</h4>
          <ul>
            <li>💬 New messages from friends</li>
            <li>👥 Friend requests</li>
            <li>📱 Status updates (optional)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default PushNotificationSettings;
